import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DatePicker } from '@/components/ui/date-picker';
import { SingleUserCombobox } from '@/components/ui/SingleUserCombobox';
import { Separator } from '@/components/ui/separator';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { useTenancy } from '@/contexts/TenancyContext';
import { Loader2, Trash2 } from 'lucide-react';

import * as AssuranceService from '@/services/assuranceActionsService';
import { AssuranceTask } from '@/services/assuranceActionsService';
import { FrameworkCombobox, FrameworksProvider, cleanStandardName } from '@/components/ui/FrameworkCombobox';
import { MCMMappingsCard } from '@/components/mcm/MCMMappingsCard';
import { ControlCombobox, ControlsProvider } from '@/components/ui/ControlCombobox';

// User name display component
const UserNameDisplay: React.FC<{ userId: string }> = ({ userId }) => {
  const { userIdToName } = useTenancy();
  return (
    <span className="text-foreground">
      {userIdToName[userId] || userId || 'Not assigned'}
    </span>
  );
};

interface EditTaskDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onTaskUpdated?: (updatedTask: Partial<AssuranceTask>, taskId: string) => void;
  onTaskDeleted?: (taskId: string) => void;
  selectedTask?: {
    id?: string;
    task?: string;
    framework?: string;
    assignee?: string;
    lastPerformedDate?: string;
    nextCheckDate?: string;
    checkInterval?: number;
    reminderDays?: number;
    evidenceType?: 'Sight Evidence' | 'Upload Evidence';
    meetingRequired?: boolean;
    status?: boolean;
  };
  isReadOnly?: boolean; // Added isReadOnly prop for read-only mode
}

interface TaskFormData {
  task: string;
  framework: string[];
  assignee: string;
  lastPerformedDate: Date;
  nextCheckDate: Date;
  checkInterval: number;
  reminderDays: number;
  evidenceType: 'Sight Evidence' | 'Upload Evidence';
  meetingRequired: boolean;
  status: boolean;
  relatedControls: string[]; // DED-1588: Add related controls field
}

const EditTaskDialog: React.FC<EditTaskDialogProps> = ({ open, onOpenChange, onTaskUpdated, onTaskDeleted, selectedTask, isReadOnly = false }) => {
  // Loading and error states
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const { activeTenancy } = useTenancy();
  const tenancyId = activeTenancy?.id || (window as any)?.companyID;
  const [taskFull, setTaskFull] = useState<any>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const taskAnchorRef = useRef<HTMLDivElement | null>(null);
  const [showTaskInHeader, setShowTaskInHeader] = useState(false);

  // Helper: Normalize any date-like value to a Date object
  function normalizeToDate(val: any): Date {
    if (!val) return new Date();
    if (val === 'Not Set') return new Date();
    if (val instanceof Date) return val;
    if (typeof val === 'string') {
      try {
        return new Date(val);
      } catch (e) {
        console.warn('Invalid date string:', val);
        return new Date();
      }
    }
    if (typeof val === 'object' && typeof val.toDate === 'function') return val.toDate();
    if (typeof val === 'object' && typeof val.seconds === 'number') return new Date(val.seconds * 1000);
    if (typeof val === 'object' && val.__time__) return new Date(val.__time__);
    return new Date();
  }

  // Map selectedTask (Task type) to formData (flat state for dialog)
  function mapTaskToFormData(task: any): TaskFormData {
    if (!task) {
      return {
        task: '',
        framework: [],
        assignee: '',
        lastPerformedDate: new Date(),
        nextCheckDate: new Date(),
        checkInterval: 365,
        reminderDays: 365,
        evidenceType: 'Sight Evidence',
        meetingRequired: false,
        status: true,
        relatedControls: [], // DED-1588
      };
    }
    // Extract from nested fields
    const checkDetail = task.checkDetail || {};
    const checkFrequency = task.checkFrequency || {};
    // Framework: support legacy and new formats
    let framework: string[] = [];
    if (checkDetail.framework && typeof checkDetail.framework === 'object') {
      // Collect all frameName values from all keys
      framework = Object.values(checkDetail.framework)
        .map((fwObj: any) => fwObj?.frameName)
        .flatMap((fw: any) => Array.isArray(fw) ? fw : typeof fw === 'string' ? [fw] : [])
        .filter(Boolean);
    } else if (typeof checkDetail.framework === 'string') {
      framework = [checkDetail.framework];
    }
    // Last performed date: from lastCheck, pick most recent
    let lastPerformedDate: Date = new Date();
    if (checkFrequency.lastCheck && typeof checkFrequency.lastCheck === 'object') {
      const lastCheckEntries = Object.values(checkFrequency.lastCheck) as any[];
      if (lastCheckEntries.length > 0 && lastCheckEntries[0]?.checkDate) {
        lastPerformedDate = normalizeToDate(lastCheckEntries[0].checkDate);
      }
    }
    // Next check date
    let nextCheckDate: Date = new Date();
    if (checkFrequency.nextCheck) {
      nextCheckDate = normalizeToDate(checkFrequency.nextCheck);
    }
    // Evidence type
    let evidenceType: 'Sight Evidence' | 'Upload Evidence' = 'Sight Evidence';
    if (typeof checkDetail.evidence === 'string') {
      evidenceType = checkDetail.evidence === 'Upload Evidence' ? 'Upload Evidence' : 'Sight Evidence';
    }
    // Meeting required
    let meetingRequired = false;
    if (typeof checkDetail.meetingNeeded === 'string') {
      meetingRequired = checkDetail.meetingNeeded === 'true';
    }
    // Status
    let status = true;
    if (typeof checkDetail.active === 'string') {
      status = checkDetail.active === 'true' || checkDetail.active === 'current';
    }
    // DED-1588: Extract related controls from task data
    const relatedControls: string[] = [];
    // From checkDetail.procRef
    if (Array.isArray(checkDetail.procRef)) {
      relatedControls.push(...checkDetail.procRef.map(String));
    }
    // From top-level relatedControls
    if (Array.isArray(task.relatedControls)) {
      relatedControls.push(...task.relatedControls.map(String));
    }
    // From selectedControls
    if (Array.isArray(task.selectedControls)) {
      relatedControls.push(...task.selectedControls.map(String));
    }
    // From framework controls
    if (checkDetail.framework && typeof checkDetail.framework === 'object') {
      Object.values(checkDetail.framework).forEach((fw: any) => {
        if (Array.isArray(fw?.controls)) {
          relatedControls.push(...fw.controls.map(String));
        }
      });
    }
    // Deduplicate
    const uniqueRelatedControls = Array.from(new Set(relatedControls.filter(Boolean)));

    return {
      task: checkDetail.check || '',
      framework,
      assignee: checkDetail.assignedTo || '',
      lastPerformedDate,
      nextCheckDate,
      checkInterval: parseInt(checkFrequency.checkDays) || 365,
      reminderDays: parseInt(checkFrequency.reminderDays) || 365,
      evidenceType,
      meetingRequired,
      status,
      relatedControls: uniqueRelatedControls, // DED-1588
    };
  }

  // Form state
  const [formData, setFormData] = useState<TaskFormData>(() => mapTaskToFormData(selectedTask));

  // Reset form when selectedTask changes
  useEffect(() => {
    setFormData(mapTaskToFormData(selectedTask));
  }, [selectedTask]);

  // Hydrate full task on open to access nested control refs (e.g., checkDetail.procRef)
  useEffect(() => {
    const hydrate = async () => {
      try {
        const idOrAssId = (selectedTask as any)?.id || (selectedTask as any)?.assID;
        if (!open || !idOrAssId) { setTaskFull(null); return; }
        const full = await AssuranceService.getTask(idOrAssId);
        if (full) setTaskFull(full);
      } catch (e) {
        console.warn('[EditTaskDialog] Failed to hydrate assurance task', e);
        setTaskFull(null);
      }
    };
    hydrate();
  }, [open, (selectedTask as any)?.id, (selectedTask as any)?.assID]);

  // Unified source for control IDs and linking
  const taskBase: any = taskFull || selectedTask || {};

  useEffect(() => {
    const el = contentRef.current;
    const anchor = taskAnchorRef.current;
    if (!el || !anchor) return;
    const onScroll = () => {
      const top = anchor.getBoundingClientRect().top;
      const containerTop = el.getBoundingClientRect().top;
      setShowTaskInHeader(top < containerTop + 8);
    };
    onScroll();
    el.addEventListener('scroll', onScroll);
    return () => el.removeEventListener('scroll', onScroll);
  }, [open, formData.task]);

  const taskControlIds = useMemo<string[]>(() => {
    const toId = (x: any): string | null => {
      if (!x) return null;
      if (typeof x === 'string') return x.trim();
      if (typeof x === 'number') return String(x);
      if (typeof x === 'object') return (x.controlID ?? x.value ?? x.id ?? '').toString().trim() || null;
      return null;
    };
    const fwMap = (taskBase as any)?.checkDetail?.framework || {};
    const ids: string[] = [];
    Object.values(fwMap).forEach((fw: any) => {
      const arr = Array.isArray(fw?.controls) ? fw.controls : [];
      arr.forEach((x: any) => {
        const id = toId(x); if (id) ids.push(id);
      });
    });
    const single = (taskBase as any)?.checkDetail?.control;
    if (single) { const sid = toId(single); if (sid) ids.push(sid); }
    // Additional sources used by Assurance tasks
    const chkProc: any[] = Array.isArray((taskBase as any)?.checkDetail?.procRef) ? (taskBase as any).checkDetail.procRef : [];
    const topProc: any[] = Array.isArray((taskBase as any)?.procRef) ? (taskBase as any).procRef : [];
    const relCtrls: any[] = Array.isArray((taskBase as any)?.relatedControls) ? (taskBase as any).relatedControls : [];
    const selCtrls: any[] = Array.isArray((taskBase as any)?.selectedControls) ? (taskBase as any).selectedControls : [];
    [...chkProc, ...topProc, ...relCtrls, ...selCtrls].forEach((x) => { const id = toId(x); if (id) ids.push(id); });
    return Array.from(new Set(ids.filter(Boolean)));
  }, [
    JSON.stringify((taskBase as any)?.checkDetail?.framework || {}),
    (taskBase as any)?.checkDetail?.control,
    JSON.stringify((taskBase as any)?.checkDetail?.procRef || []),
    JSON.stringify((taskBase as any)?.procRef || []),
    JSON.stringify((taskBase as any)?.relatedControls || []),
    JSON.stringify((taskBase as any)?.selectedControls || []),
  ]);

  const ensureControlLinked = useCallback(async (controlId: string) => {
    if (!selectedTask?.id) return;
    const currentFW = ((taskBase as any)?.checkDetail?.framework) || {};
    const keys = Object.keys(currentFW);
    const firstKey = keys[0] || '0';
    const frameName = (currentFW[firstKey]?.frameName) || (Array.isArray(formData.framework) && formData.framework[0]) || 'Custom';
    const existing: string[] = Array.isArray(currentFW[firstKey]?.controls) ? currentFW[firstKey].controls.map(String) : [];
    if (existing.includes(controlId)) return;
    const updatedFW = {
      ...currentFW,
      [firstKey]: {
        ...(currentFW[firstKey] || {}),
        frameName,
        controls: Array.from(new Set([...existing, String(controlId)]))
      }
    } as any;
    // Also update checkDetail.procRef as a canonical list for task → control links
    const currentChkProc: string[] = Array.isArray((taskBase as any)?.checkDetail?.procRef)
      ? (taskBase as any).checkDetail.procRef.map(String)
      : [];
    const updatedChkProc = Array.from(new Set([...currentChkProc, String(controlId)]));
    const fullCheckDetail = {
      active: (taskBase as any)?.checkDetail?.active ?? 'true',
      assignedTo: (taskBase as any)?.checkDetail?.assignedTo ?? '',
      check: (taskBase as any)?.checkDetail?.check ?? '',
      evidence: (taskBase as any)?.checkDetail?.evidence ?? 'Sight Evidence',
      framework: updatedFW,
      procRef: updatedChkProc,
      meetingNeeded: (taskBase as any)?.checkDetail?.meetingNeeded,
      frameworkSkipped: (taskBase as any)?.checkDetail?.frameworkSkipped,
    } as any;
    const res = await AssuranceService.updateTask(selectedTask.id, { checkDetail: fullCheckDetail });
    if (onTaskUpdated) onTaskUpdated({ checkDetail: { framework: updatedFW, procRef: updatedChkProc } as any }, selectedTask.id);
  }, [selectedTask?.id, JSON.stringify((selectedTask as any)?.checkDetail?.framework || {}), JSON.stringify(formData.framework), onTaskUpdated]);

  // Handle soft delete
  const handleDelete = async () => {
    if (!selectedTask?.id) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const result = await AssuranceService.softDeleteTask(selectedTask.id);
      
      if (result?.success) {
        console.log('Task deleted successfully');
        // Call the callback to update parent component state
        if (onTaskDeleted) {
          onTaskDeleted(selectedTask.id);
        }
        onOpenChange(false);
      } else {
        setError('Failed to delete task. Please try again.');
      }
    } catch (err) {
      console.error('Error deleting task:', err);
      setError('Failed to delete task. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle form submission
  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);

      // Convert form data to AssuranceTask format
      // DED-1588: Use formData.relatedControls instead of taskControlIds for user-edited controls
      const controlsToSave = formData.relatedControls.length > 0 ? formData.relatedControls : taskControlIds;
      const taskData: Partial<AssuranceTask> = {
        checkDetail: {
          active: formData.status ? 'true' : 'false',
          assignedTo: formData.assignee,
          check: formData.task,
          // DED-1026: Store framework name in uppercase for consistent filtering
          framework:
            formData.framework.length === 1
              ? { 0: { frameName: cleanStandardName(formData.framework[0]).toUpperCase(), controls: controlsToSave } }
              : Object.fromEntries(
                formData.framework.map((fw, idx) => [idx, { frameName: cleanStandardName(fw).toUpperCase(), controls: controlsToSave }])
              ),
          meetingNeeded: formData.meetingRequired ? 'true' : 'false',
          evidence: formData.evidenceType === 'Upload Evidence' ? 'Upload Evidence' : 'Sight Evidence',
          procRef: controlsToSave, // DED-1588: Save related controls to procRef
        } as any, // DED-1588: Type assertion needed as procRef is not in the type definition but is used in the data model
        checkFrequency: {
          checkDays: formData.checkInterval.toString(),
          reminderDays: formData.reminderDays.toString(),
          sendReminder: 'true',
          lastCheck: {},
          nextCheck: formData.nextCheckDate
        },
        relatedControls: controlsToSave, // DED-1588: Also save to top-level relatedControls
      } as any;
      // Create new task or update existing one
      if (selectedTask?.id) {
        await AssuranceService.updateTask(selectedTask.id, taskData);
        console.log('Task updated successfully');
        // Call the callback to update parent component state
        if (onTaskUpdated) {
          onTaskUpdated(taskData, selectedTask.id);
        }
      } else {
        const response = await AssuranceService.createTask(taskData);
        console.log('Task created successfully');
        // Call the callback for newly created tasks if ID is returned
        if (onTaskUpdated && response && response.id) {
          onTaskUpdated(taskData, response.id);
        }
      }

      onOpenChange(false);
    } catch (err) {
      console.error('Error saving task:', err);
      setError('Failed to save task. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent ref={contentRef} className="max-w-4xl! bg-card/95 backdrop-blur-xl rounded-xl w-full h-[80vh] overflow-y-auto shadow-2xl border border-white/10 flex flex-col p-0!">
        <div className="flex flex-col h-full">
          <div className="sticky top-0 z-20 p-4 py-6 border-b border-white/10 bg-gradient-to-b from-primary/5 to-transparent backdrop-blur supports-[backdrop-filter]:bg-white/60">
            <DialogTitle>{showTaskInHeader ? (formData.task || 'Assurance Task') : (isReadOnly ? 'View Assurance Task' : 'Edit Assurance Task')}</DialogTitle>
            <DialogDescription>
              {isReadOnly ? 'View the details of this assurance task.' : 'Edit the details of this assurance task.'}
            </DialogDescription>
          </div>
          <div className="grid gap-4 py-4 px-4">
            <div ref={taskAnchorRef} className="h-0" />
            {/* Task */}
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="task" className="col-span-1 text-sm font-medium text-gray-600 dark:text-foreground whitespace-normal break-words">Task Name</Label>
              <div className="col-span-3">
                {isReadOnly ? (
                  <div className="p-2 border rounded-md bg-muted/20">{formData.task || 'Not set'}</div>
                ) : (
                  <Textarea
                    id="task"
                    value={formData.task || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, task: e.target.value }))}
                    placeholder="Task name"
                  />
                )}
              </div>
            </div>

            {/* Framework */}
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="framework" className="col-span-1 text-sm font-medium text-gray-600 dark:text-foreground whitespace-normal break-words">Framework</Label>
              <div className="col-span-3">
                {isReadOnly ? (
                  <div className="p-2 border rounded-md bg-muted/20">{formData.framework.length ? formData.framework.join(', ') : 'Not set'}</div>
                ) : (

                  <>
                    {/* <Input
                    id="framework"
                    value={formData.framework || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, framework: e.target.value }))}
                    placeholder="Framework"
                  /> */}
                    <FrameworksProvider>
                      <FrameworkCombobox
                        selected={Array.isArray(formData.framework) ? formData.framework : formData.framework ? [formData.framework] : []}
                        setSelected={(frameworkArr) => setFormData(prev => ({ ...prev, framework: Array.isArray(frameworkArr) ? frameworkArr : [frameworkArr] }))}
                      />
                    </FrameworksProvider>
                  </>
                )}
              </div>
            </div>

            {/* DED-1588: Related Controls */}
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="relatedControls" className="col-span-1 text-sm font-medium text-gray-600 dark:text-foreground whitespace-normal break-words">Related Controls</Label>
              <div className="col-span-3">
                {isReadOnly ? (
                  <div className="p-2 border rounded-md bg-muted/20">
                    {formData.relatedControls.length > 0 ? formData.relatedControls.join(', ') : 'No controls linked'}
                  </div>
                ) : (
                  <ControlsProvider>
                    <ControlCombobox
                      selected={formData.relatedControls}
                      setSelected={(controls) => setFormData(prev => ({ ...prev, relatedControls: controls.map(String) }))}
                      placeholder="Select related controls..."
                      fullWidth
                    />
                  </ControlsProvider>
                )}
              </div>
            </div>

            {/* Assignee */}
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="assignee" className="col-span-1 text-sm font-medium text-gray-600 dark:text-foreground whitespace-normal break-words">Assignee</Label>
              <div className="col-span-3">
                {isReadOnly ? (
                  <div className="p-2 border rounded-md bg-muted/20">
                    {formData.assignee ? (
                      <div className="flex items-center gap-2 text-sm">
                        {/* Display user name without interactive dropdown */}
                        <UserNameDisplay userId={formData.assignee} />
                      </div>
                    ) : 'Not assigned'}
                  </div>
                ) : (
                  <SingleUserCombobox
                    selected={formData.assignee || ''}
                    setSelected={(userId) => setFormData(prev => ({ ...prev, assignee: userId }))}
                  />
                )}
              </div>
            </div>

            {/* Last Check */}
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="lastPerformedDate" className="col-span-1 text-sm font-medium text-gray-600 dark:text-foreground whitespace-normal break-words">Last Performed</Label>
              <div className="col-span-3">
                {isReadOnly ? (
                  <div className="p-2 border rounded-md bg-muted/20">
                    {formData.lastPerformedDate ? formData.lastPerformedDate.toLocaleDateString() : 'Not set'}
                  </div>
                ) : (
                  <DatePicker
                    date={formData.lastPerformedDate}
                    setDate={(date) => date && setFormData(prev => ({ ...prev, lastPerformedDate: date }))}
                  />
                )}
              </div>
            </div>

            {/* Next Check */}
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="nextCheckDate" className="col-span-1 text-sm font-medium text-gray-600 dark:text-foreground whitespace-normal break-words">Next Check</Label>
              <div className="col-span-3">
                {isReadOnly ? (
                  <div className="p-2 border rounded-md bg-muted/20">
                    {formData.nextCheckDate ? formData.nextCheckDate.toLocaleDateString() : 'Not set'}
                  </div>
                ) : (
                  <DatePicker
                    date={formData.nextCheckDate}
                    setDate={(date) => date && setFormData(prev => ({ ...prev, nextCheckDate: date }))}
                  />
                )}
              </div>
            </div>

            {/* Check Interval */}
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="checkInterval" className="col-span-1 text-sm font-medium text-gray-600 dark:text-foreground whitespace-normal break-words">Check Interval (days)</Label>
              <div className="col-span-3">
                {isReadOnly ? (
                  <div className="p-2 border rounded-md bg-muted/20">{formData.checkInterval || 0} days</div>
                ) : (
                  <Input
                    id="checkInterval"
                    type="number"
                    value={formData.checkInterval || 0}
                    onChange={(e) => setFormData(prev => ({ ...prev, checkInterval: parseInt(e.target.value) || 0 }))}
                    placeholder="Check interval in days"
                  />
                )}
              </div>
            </div>

            {/* Reminder Days */}
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="reminderDays" className="col-span-1 text-sm font-medium text-gray-600 dark:text-foreground whitespace-normal break-words">Reminder Days</Label>
              <div className="col-span-3">
                {isReadOnly ? (
                  <div className="p-2 border rounded-md bg-muted/20">{formData.reminderDays || 0} days</div>
                ) : (
                  <Input
                    id="reminderDays"
                    type="number"
                    value={formData.reminderDays || 0}
                    onChange={(e) => setFormData(prev => ({ ...prev, reminderDays: parseInt(e.target.value) || 0 }))}
                    placeholder="Reminder days before check"
                  />
                )}
              </div>
            </div>

            <div className="flex flex-row gap-4 w-full">
              {/* Evidence Type */}
              <div className="flex flex-col gap-1 w-1/3">
                <Label htmlFor="evidenceType" className="col-span-1 text-sm font-medium text-gray-600 dark:text-foreground whitespace-normal break-words">Evidence Type</Label>
                <div className="w-full">
                  {isReadOnly ? (
                    <div className="p-2 border rounded-md bg-muted/20">{formData.evidenceType || 'Not set'}</div>
                  ) : (
                    <Select
                      value={formData.evidenceType || 'select-evidence'}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, evidenceType: value as 'Sight Evidence' | 'Upload Evidence' }))}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select Evidence Type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="select-evidence" disabled>Select Evidence Type</SelectItem>
                        <SelectItem value="Sight Evidence">Sight Evidence</SelectItem>
                        <SelectItem value="Upload Evidence">Upload Evidence</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                </div>
              </div>
              {/* Meeting Required */}
              <div className="flex flex-col gap-1 w-1/3">
                <Label htmlFor="meetingRequired" className="col-span-1 text-sm font-medium text-gray-600 dark:text-foreground whitespace-normal break-words">Meeting Required</Label>
                <div className="w-full">
                  {isReadOnly ? (
                    <div className="p-2 border rounded-md bg-muted/20">{formData.meetingRequired ? 'Yes' : 'No'}</div>
                  ) : (
                    <Select
                      value={formData.meetingRequired ? 'true' : 'false'}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, meetingRequired: value === 'true' }))}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Is a Meeting Required?" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="select-meeting" disabled>Is a Meeting Required?</SelectItem>
                        <SelectItem value="true">Yes</SelectItem>
                        <SelectItem value="false">No</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                </div>
              </div>

              {/* Status */}
              <div className="flex flex-col gap-1 w-1/3">
                <Label htmlFor="status" className="col-span-1 text-sm font-medium text-gray-600 dark:text-foreground whitespace-normal break-words">Status</Label>
                <div className="w-full">
                  {isReadOnly ? (
                    <div className="p-2 border rounded-md bg-muted/20">{formData.status ? 'Active' : 'Inactive'}</div>
                  ) : (
                    <Select
                      value={formData.status ? 'true' : 'false'}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, status: value === 'true' }))}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select Task Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="select-status" disabled>Select Task Status</SelectItem>
                        <SelectItem value="true">Active</SelectItem>
                        <SelectItem value="false">Inactive</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                </div>
              </div>
            </div>
            {/* Error message */}
            {error && (
              <div className="bg-destructive/10 text-destructive px-4 py-3 rounded-md mb-4">
                <p className="font-medium">{error}</p>
              </div>
            )}
          </div>
          <div className="px-4">
            <MCMMappingsCard
              tenancyId={tenancyId}
              isReadOnly={isReadOnly}
              controlIds={taskControlIds}
              defaultEntityLink={{
                id: `internal:assurance:${String((taskBase as any)?.id || (taskBase as any)?.assID || '')}`,
                ref: String((taskBase as any)?.id || (taskBase as any)?.assID || ''),
                title: String((taskBase as any)?.task || (taskBase as any)?.checkDetail?.check || (taskBase as any)?.assID || 'Assurance Task'),
                domain: 'Assurance',
                relation: 'assures',
              }}
              onEnsureControlLinked={async (cid) => {
                await ensureControlLinked(cid);
              }}
            />
          </div>
          {/* Task ID */}
          {selectedTask?.id && (
            <div className="mt-4 p-4">
              <Label className="text-sm font-medium text-gray-600 dark:text-foreground">Task ID:</Label>
              <p className="text-sm text-gray-500 dark:text-gray-400">{selectedTask.id}</p>
            </div>
          )}

          <DialogFooter className="mt-4 p-4 sticky bottom-0 z-20 border-white/10 bg-gradient-to-t from-primary/5 to-transparent backdrop-blur supports-[backdrop-filter]:bg-white/60 border-t">
            {isReadOnly ? (
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Close
              </Button>
            ) : (
              <>
                <Button
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="destructive"
                      disabled={loading}
                      className="mr-auto"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Deleting...
                        </>
                      ) : (
                        <>
                          <Trash2 className="mr-2 h-4 w-4" />
                          Delete
                        </>
                      )}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete Assurance Task</AlertDialogTitle>
                      <AlertDialogDescription>
                        Are you sure you want to delete this assurance task? This action will:
                        <ul className="list-disc list-inside mt-2 space-y-1">
                          <li>Mark the task as deleted (it won't appear in the main list)</li>
                          <li>Preserve all task data and history</li>
                          <li>Allow the task to be restored later if needed</li>
                          <li>Record who deleted it and when</li>
                        </ul>
                        <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800">
                          <strong>Note:</strong> You can view deleted tasks by long-pressing the "All Frameworks" button and restore them from the deleted view.
                        </div>
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                        Delete Task
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
                <Button
                  onClick={handleSave}
                  type="submit"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : 'Save'}
                </Button>
              </>
            )}
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EditTaskDialog;
